import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.email || !formData?.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const { data, error: signInError } = await signIn(formData?.email, formData?.password);
      
      if (signInError) {
        if (signInError?.message?.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials.');
        } else if (signInError?.message?.includes('Email not confirmed')) {
          setError('Please confirm your email address before signing in.');
        } else {
          setError(signInError?.message || 'Failed to sign in. Please try again.');
        }
        return;
      }

      // Success - redirect to dashboard or home
      navigate('/patient-portal');
      
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const demoCredentials = [
    { email: 'admin@ayurveda.com', password: 'admin123', role: 'Admin' },
    { email: 'doctor@ayurveda.com', password: 'doctor123', role: 'Doctor' },
    { email: 'patient@ayurveda.com', password: 'patient123', role: 'Patient' }
  ];

  const fillDemoCredentials = (email, password) => {
    setFormData({ email, password });
    setError('');
  };

  return (
    <>
      <Helmet>
        <title>Login - Sri Dhanwantari AyurVaidik Parishkar Kendra</title>
        <meta name="description" content="Login to your account at Sri Dhanwantari AyurVaidik Parishkar Kendra to access your treatment history, appointments, and personalized care plans." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-md mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-8"
              >
                <h1 className="font-headline text-3xl font-bold mb-4 text-primary">
                  Welcome Back
                </h1>
                <p className="text-muted-foreground">
                  Sign in to access your patient portal and continue your healing journey.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="glass-card-readable p-8"
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-error font-medium text-sm">{error}</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                      Email Address
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData?.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData?.password}
                        onChange={handleInputChange}
                        placeholder="Enter your password"
                        required
                        className="w-full pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full ayurveda-gradient text-white font-semibold py-3"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Signing In...
                      </div>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-muted-foreground text-sm">
                    Don't have an account?{' '}
                    <Link to="/signup" className="text-primary hover:text-accent font-medium">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </motion.div>

              {/* Demo Credentials Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mt-8 glass-panel p-6"
              >
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Users" size={20} className="text-primary" />
                  Demo Credentials
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Use these credentials to explore the platform:
                </p>
                <div className="space-y-3">
                  {demoCredentials?.map((cred, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-background/50 rounded-lg border border-border"
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-foreground">{cred?.role}</div>
                        <div className="text-xs text-muted-foreground">
                          {cred?.email} / {cred?.password}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fillDemoCredentials(cred?.email, cred?.password)}
                        className="border-primary/30 text-primary hover:bg-primary/10 text-xs"
                      >
                        Use
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Click "Use" to automatically fill the credentials, then click "Sign In".
                </p>
              </motion.div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Login;